import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Clock, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  // Dil değiştiğinde haritayı yeniden yükle
  useEffect(() => {
    setMapKey(prev => prev + 1);
  }, [language]);

  const mapUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2428.405736289089!2d13.404953976891595!3d52.520006971810336!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a84e373f035901%3A0x42120465b5e3b70!2sBerlin%2C%20Germany!5e0!3m2!1s${language}!2sde!4v1703000000000!5m2!1s${language}!2sde`;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CAPTCHA kontrolü
    if (!captchaChecked) {
      setShowCaptchaError(true);
      return;
    }
    setShowCaptchaError(false);
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '18516033-0ab7-4642-8d78-45d2bf7a6e54',
          subject: formData.subject || 'Kontaktformular Anfrage',
          from_name: 'SLÁI Events Website',
          to: 'info@slaievents.com',
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(t.contact.successTitle, {
          description: t.contact.successDescription,
        });

        // Reset form and captcha
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setCaptchaChecked(false);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error(
        language === 'de' ? 'Fehler' : 'Error',
        {
          description: language === 'de' ? 'Es gab ein Problem beim Senden. Bitte versuchen Sie es erneut.' : 'There was a problem sending. Please try again.',
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: t.contact.infoCards.email,
      details: t.company.email,
      link: `mailto:${t.company.email}`
    },
    {
      icon: MapPin,
      title: t.contact.infoCards.location,
      details: `${t.company.addressLine1}\n${t.company.addressLine2}`,
      link: '#'
    },
    {
      icon: Clock,
      title: t.contact.infoCards.hours,
      details: t.company.hours,
      link: '#'
    }
  ];

  return (
    <div key={language} className="min-h-screen pt-16 bg-[#f8f6f3]">
      {/* Hero Section */}
      <section className="py-12 sm:py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'KONTAKTIEREN SIE UNS' : 'CONTACT US'}
            </p>
            <h1 className="text-4xl sm:text-6xl md:text-7xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.contact.title}
            </h1>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-6" />
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {t.contact.subtitle}
            </p>
          </div>

          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const isEmail = info.link.startsWith('mailto:');
              const CardWrapper = isEmail ? 'a' : 'div';
              return (
                <CardWrapper
                  key={index}
                  href={isEmail ? info.link : undefined}
                  className={isEmail ? 'block' : ''}
                >
                  <Card
                    className="rounded-xl border border-white/20 bg-[#252525] shadow-none hover:bg-[#2a2a2a] transition-all duration-300 group h-full"
                  >
                    <CardContent className="p-6 sm:p-8 text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#1a1a1a] flex items-center justify-center mx-auto mb-4 group-hover:bg-white transition-all duration-300">
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white group-hover:text-[#1a1a1a] transition-colors duration-300" />
                      </div>
                      <h3 className="text-xs tracking-[0.15em] uppercase text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{info.title}</h3>
                      <p className="text-sm text-white/60 whitespace-pre-line" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{info.details}</p>
                    </CardContent>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 sm:py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Card key={language} className="rounded-xl border border-white/20 bg-[#252525] shadow-none">
              <CardHeader className="text-center pb-6 border-b border-white/10 px-6 sm:px-8">
                <CardTitle className="text-3xl sm:text-4xl text-white" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}>{t.contact.formTitle}</CardTitle>
                <CardDescription className="text-sm mt-3 text-white/60" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  {t.contact.formSubtitle}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8 px-6 sm:px-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs tracking-[0.1em] uppercase text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.contact.formLabels.name} *</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder={t.contact.formPlaceholders.name}
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40 focus:ring-[#e67e22] focus:border-[#e67e22] rounded-none"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs tracking-[0.1em] uppercase text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.contact.formLabels.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder={t.contact.formPlaceholders.email}
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40 focus:ring-[#e67e22] focus:border-[#e67e22] rounded-none"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-xs tracking-[0.1em] uppercase text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.contact.formLabels.subject} *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      placeholder={t.contact.formPlaceholders.subject}
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40 focus:ring-[#e67e22] focus:border-[#e67e22] rounded-none"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs tracking-[0.1em] uppercase text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.contact.formLabels.message} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder={t.contact.formPlaceholders.message}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40 focus:ring-[#e67e22] focus:border-[#e67e22] rounded-none"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    />
                  </div>
                  <div className="py-2">
                    <div className="flex items-center space-x-3">
                      <Checkbox 
                        id="captcha" 
                        checked={captchaChecked}
                        onCheckedChange={(checked) => {
                          setCaptchaChecked(checked);
                          if (checked) setShowCaptchaError(false);
                        }}
                        className="border-white/40 data-[state=checked]:bg-[#e67e22] data-[state=checked]:border-[#e67e22] rounded-none"
                      />
                      <Label htmlFor="captcha" className="text-sm text-white/70 cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        {language === 'de' ? 'Ich bin kein Roboter' : 'I am not a robot'}
                      </Label>
                    </div>
                    {showCaptchaError && (
                      <p className="text-red-500 text-sm mt-2">
                        {language === 'de' ? '⚠️ Bitte bestätigen Sie, dass Sie kein Roboter sind.' : '⚠️ Please confirm that you are not a robot.'}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] text-xs tracking-[0.2em] uppercase py-6 rounded-none"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    disabled={isSubmitting}
                  >
                    <Send className="w-4 h-4 mr-3" />
                    {isSubmitting ? 'Wird gesendet...' : t.contact.sendButton}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-16 sm:py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4 text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'STANDORT' : 'LOCATION'}
            </p>
            <h2 className="text-4xl text-white mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.contact.mapTitle}
            </h2>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-12" />
            <div className="bg-white border border-[#e8e4df] h-96 shadow-none overflow-hidden">
              <iframe 
                key={mapKey}
                src={mapUrl}
                width="100%" 
                height="100%" 
                style={{ border: 0, filter: 'grayscale(100%) contrast(1.1)' }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="SLÁI Events Location"
              ></iframe>
            </div>
            <p className="text-center text-white/60 mt-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              <MapPin className="w-4 h-4 text-[#8b7355] inline-block mr-2" />
              {t.contact.mapDescription}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
